import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useChat } from 'ai/react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import ArticleIcon from '@mui/icons-material/Article';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EventIcon from '@mui/icons-material/Event';
import ArticleFormModal from '../components/ArticleFormModal'; // Import the modal
import ChatGroupPostModal from '../components/ChatGroupPostModal'; // <-- Import the new modal
import EventFormModal from '../components/EventFormModal'; // Import Event modal
import { useAuth } from '../context/AuthContext'; // To get the token
import { useGenerateArticleFromChat, useGenerateChatGroupPost, useGenerateEventFromChat } from '../services/appService'; // <-- Include event generation hook

// Helper to render messages with simple markdown-like formatting
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Paper 
        elevation={1} 
        sx={{ 
          p: 1.5, 
          maxWidth: '70%', 
          bgcolor: isUser ? 'primary.light' : 'grey.200',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: isUser ? '10px 10px 0 10px' : '10px 10px 10px 0',
        }}
      >
        {/* Basic rendering - replace newline characters. Needs more robust markdown later */}
        {message.content.split('\n').map((line, index) => (
            <Typography key={index} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {line}
            </Typography>
        ))}
      </Paper>
    </Box>
  );
};


function ChatPage() {
  const { appId } = useParams();
  const { token } = useAuth(); // Get the auth token
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [generatedArticleData, setGeneratedArticleData] = useState(null); // To hold { title, content }
  const [articleGenerationLoading, setArticleGenerationLoading] = useState(false);
  const [articleGenerationError, setArticleGenerationError] = useState('');

  // State for Chat Group Post Modal
  const [chatGroupPostModalOpen, setChatGroupPostModalOpen] = useState(false); // <-- New state
  const [generatedPostText, setGeneratedPostText] = useState(''); // <-- New state
  const [groupPostGenerationLoading, setGroupPostGenerationLoading] = useState(false); // <-- New state
  const [groupPostGenerationError, setGroupPostGenerationError] = useState(''); // <-- New state

  // State for Event Generation Modal
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [generatedEventData, setGeneratedEventData] = useState(null);
  const [eventGenerationLoading, setEventGenerationLoading] = useState(false);
  const [eventGenerationError, setEventGenerationError] = useState('');

  const chatContainerRef = useRef(null);

  // Hooks for generation API calls
  const generateArticleHook = useGenerateArticleFromChat();
  const generateChatGroupPostHook = useGenerateChatGroupPost(); // <-- New hook instance
  const generateEventHook = useGenerateEventFromChat(); // Event generation hook

  const { messages, input, handleInputChange, handleSubmit, isLoading, error: chatError, setMessages } = useChat({
    api: `/api/apps/${appId}/chat/stream`, // Backend endpoint for chat streaming
    headers: {
         // Include the Authorization header for the streaming API call
        'Authorization': `Bearer ${token}` 
    },
     // Send the entire message history on submit
    // body: { messages }, // REMOVED: useChat sends messages automatically
    onError: (err) => {
      console.error("Chat stream error:", err);
      // Display a more user-friendly error if needed
    },
     onFinish: () => {
        console.log("Chat stream finished.");
     }
  });

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleGenerateArticle = async () => {
      setArticleGenerationLoading(true);
      setArticleGenerationError('');
      setGeneratedArticleData(null);

      if (!messages || messages.length === 0) {
          setArticleGenerationError("Kein Chatverlauf zum Erstellen eines Artikels vorhanden.");
          setArticleGenerationLoading(false);
          return;
      }

      try {
          console.log("Sending messages to generate article:", messages);
          const responseData = await generateArticleHook(appId, messages);
          
          console.log("Received generated article data:", responseData);

          if (responseData && responseData.title && responseData.content) {
              setGeneratedArticleData({
                  // Set initial data for the ArticleFormModal
                  title: responseData.title,
                  content: responseData.content,
                  // Default other fields as needed for a *new* article
                  filter_name: '', // Let user choose or set a default
                  image_url: '',
                  preview_image_url: '',
                  is_published: false, // Default to not published
                  organization_id: '',
                  is_pinned: false,
              });
              setArticleModalOpen(true); // Open the modal with pre-filled data
          } else {
              console.error("Generated article data is incomplete:", responseData);
               const errorMessage = responseData?.rawOutput 
                 ? `Fehler beim Parsen der Antwort des Modells: ${responseData.rawOutput}`
                 : "Generierte Artikeldaten sind unvollständig.";
              setArticleGenerationError(errorMessage);
          }
      } catch (err) {
          console.error("Error generating article:", err);
          const errMsg = err.message || "Unbekannter Fehler beim Erstellen des Artikels.";
          const rawOutput = err.rawOutput; // Assuming hook might add this
          setArticleGenerationError(rawOutput ? `${errMsg}: ${rawOutput}` : errMsg);
      } finally {
          setArticleGenerationLoading(false);
      }
  };

  const handleCloseArticleModal = () => {
      setArticleModalOpen(false);
      setGeneratedArticleData(null); // Clear data when modal closes
  };

  const handleSaveArticle = () => {
      // This function is passed to the modal, which calls it on successful save.
      // We just need to close the modal here. Refreshing articles happens on ArticlesPage.
      handleCloseArticleModal();
      // Optionally, clear the chat or add a system message?
       // setMessages([...messages, { id: Date.now().toString(), role: 'system', content: 'Artikel wurde erstellt.' }]);
  };

  // --- Chat Group Post Generation Logic --- <-- NEW SECTION
  const handleGenerateChatGroupPost = async () => {
    setGroupPostGenerationLoading(true);
    setGroupPostGenerationError('');
    setGeneratedPostText('');

    if (!messages || messages.length === 0) {
        setGroupPostGenerationError("Kein Chatverlauf zum Erstellen eines Beitrags vorhanden.");
        setGroupPostGenerationLoading(false);
        return;
    }

    try {
        console.log("Sending messages to generate chat group post:", messages);
        const responseData = await generateChatGroupPostHook(appId, messages);
        
        console.log("Received generated post data:", responseData);

        if (responseData && responseData.postText) {
            setGeneratedPostText(responseData.postText);
            setChatGroupPostModalOpen(true); // Open the new modal
        } else {
            console.error("Generated post data is incomplete or missing:", responseData);
             const errorMessage = responseData?.rawOutput 
               ? `Fehler beim Parsen der Antwort des Modells: ${responseData.rawOutput}`
               : "Generierte Beitragsdaten sind unvollständig.";
            setGroupPostGenerationError(errorMessage);
        }
    } catch (err) {
        console.error("Error generating chat group post:", err);
        const errMsg = err.message || "Unbekannter Fehler beim Erstellen des Beitrags.";
         const rawOutput = err.rawOutput; 
        setGroupPostGenerationError(rawOutput ? `${errMsg}: ${rawOutput}` : errMsg);
    } finally {
        setGroupPostGenerationLoading(false);
    }
  };

  const handleCloseChatGroupPostModal = () => {
      setChatGroupPostModalOpen(false);
      setGeneratedPostText(''); // Clear generated text on close
  };
  // No specific save handler needed here, modal handles the sending
  // --- END NEW SECTION ---

  // --- Event Generation Logic ---
  const handleGenerateEvent = async () => {
    setEventGenerationLoading(true);
    setEventGenerationError('');
    setGeneratedEventData(null);

    if (!messages || messages.length === 0) {
        setEventGenerationError("Kein Chatverlauf zum Erstellen eines Events vorhanden.");
        setEventGenerationLoading(false);
        return;
    }

    try {
        const responseData = await generateEventHook(appId, messages);
        if (responseData && responseData.title && responseData.date && responseData.time) {
            // Map response to EventFormModal expected shape
            const prefill = {
               title: responseData.title,
               description: responseData.description || '',
               date: responseData.date,
               time: responseData.time,
               location: responseData.location || '',
               category: responseData.category || '',
               is_published: false
            };
            setGeneratedEventData(prefill);
            setEventModalOpen(true);
        } else {
            setEventGenerationError("Generierte Eventdaten sind unvollständig.");
        }
    } catch (err) {
        const raw = err.rawOutput;
        const msg = err.message || 'Fehler bei der Event-Erstellung';
        setEventGenerationError(raw ? `${msg}: ${raw}` : msg);
    } finally {
        setEventGenerationLoading(false);
    }
  };

  const handleCloseEventModal = () => {
       setEventModalOpen(false);
       setGeneratedEventData(null);
  };

  const handleSaveEvent = () => {
       handleCloseEventModal();
  };

  // Combine all errors for the main alert display
  const combinedError = articleGenerationError || groupPostGenerationError || eventGenerationError || chatError?.message;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ flexShrink: 0 }}>
        AI Content Assistant Chat
      </Typography>
      
      {combinedError && (
          <Alert 
            severity="error" 
            sx={{ mb: 2, flexShrink: 0 }} 
            onClose={() => {
              setArticleGenerationError(''); 
              setGroupPostGenerationError('');
              setEventGenerationError('');
              // chatError is read-only from useChat, cannot be cleared here
            }}
          >
              {combinedError || "Ein Fehler ist aufgetreten."}
          </Alert>
      )}

      {/* Message Display Area */}
      <Box 
        ref={chatContainerRef}
        sx={{ 
          flexGrow: 1, 
          overflowY: 'auto', 
          mb: 2, 
          p: 2, 
          border: '1px solid', 
          borderColor: 'grey.300', 
          borderRadius: 1,
          bgcolor: 'background.paper' 
        }}
      >
        {messages.length === 0 && !isLoading && (
             <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
                 Beginnen Sie das Gespräch, indem Sie eine Nachricht eingeben.
             </Typography>
        )}
        {messages.map(m => (
          <ChatMessage key={m.id} message={m} />
        ))}
         {isLoading && messages[messages.length -1]?.role === 'user' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                 <CircularProgress size={20} sx={{ ml: 1, mt: 1 }} />
            </Box>
         )}
      </Box>

      {/* Input Area */}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Fragen Sie etwas oder geben Sie Anweisungen..."
          value={input}
          onChange={handleInputChange}
          disabled={isLoading || articleGenerationLoading || groupPostGenerationLoading || eventGenerationLoading} // <-- Include event generation loading
          multiline
          maxRows={4} // Allow multi-line input
        />
        <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading || articleGenerationLoading || groupPostGenerationLoading || eventGenerationLoading || !input.trim()} // <-- Include event generation loading
            sx={{ height: '56px' }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </Button>
      </Box>

      {/* Action Buttons */}
       <Box sx={{ display: 'flex', gap: 1, mt: 2, flexShrink: 0 }}>
            <Button 
                variant="outlined" 
                startIcon={articleGenerationLoading ? <CircularProgress size={20} /> : <ArticleIcon />}
                onClick={handleGenerateArticle}
                disabled={isLoading || articleGenerationLoading || groupPostGenerationLoading || messages.length === 0} // <-- Updated disabled state
            >
                Artikel erstellen
            </Button>
            <Button 
                variant="outlined" 
                startIcon={groupPostGenerationLoading ? <CircularProgress size={20} /> : <GroupAddIcon />} // <-- Updated icon/loading state
                onClick={handleGenerateChatGroupPost} // <-- Updated onClick handler
                disabled={isLoading || articleGenerationLoading || groupPostGenerationLoading || messages.length === 0} // <-- Updated disabled state
            > 
                Chatgruppen-Post erstellen
            </Button>
            <Button 
                variant="outlined" 
                startIcon={eventGenerationLoading ? <CircularProgress size={20} /> : <EventIcon />} 
                onClick={handleGenerateEvent}
                disabled={isLoading || articleGenerationLoading || groupPostGenerationLoading || eventGenerationLoading || messages.length === 0}
            >
                Event erstellen
            </Button>
        </Box>

        {/* Article Generation Modal */}
        {/* Pass the generated data as the 'article' prop */}
        {/* Ensure the modal knows this is for *creating* a new article */}
         <ArticleFormModal
            open={articleModalOpen}
            onClose={handleCloseArticleModal}
            onSave={handleSaveArticle} // Function called after successful save in modal
            article={generatedArticleData} // Pass generated data (or null if not generated)
            appId={appId}
            // Explicitly indicate creation mode if necessary, though `article` having no ID should suffice
            // isEditMode={false} // Might not be needed if modal checks article.id
        />
        
        {/* Chat Group Post Modal */} {/* <-- Render the new modal */} 
        <ChatGroupPostModal
            open={chatGroupPostModalOpen}
            onClose={handleCloseChatGroupPostModal}
            appId={appId}
            initialText={generatedPostText} // Pass generated text
        />

        {/* Event Generation Modal */}
        <EventFormModal
            open={eventModalOpen}
            onClose={handleCloseEventModal}
            onSave={handleSaveEvent}
            event={generatedEventData}
            appId={appId}
        />

    </Box>
  );
}

export default ChatPage; 